/**
 * Grouped dashboard table — group rows (Kunde or ProduktType) and the instance
 * rows inside an expanded group.
 *
 * Notifications are created by a confirmed product assessment and sent from the
 * Benachrichtigungen page (DEV-100); the status cells only show how far an
 * instance is and link there.
 */

import { Plural, Trans, useLingui } from '@lingui/react/macro';
import { type MouseEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Chip } from '../../components/Chip';
import { DataTable, type DataTableColumn } from '../../components/DataTable';
import { Icon } from '../../components/Icon';
import { DASH, fmtDate, fmtVersion } from '../../lib/format';
import type { OverviewRow } from '../../lib/types';
import {
  auditLogLink,
  type GateMap,
  type OverviewGroup,
  type WorkState,
} from './dashboardGroups';
import { orFallback, type NotifyGate } from './dashboardModel';
import { ComponentsCell, CvesCell, KomponentenDetail } from './ProductCells';
import type { ExploitabilityStore } from './useExploitability';

/** Rows per page; below that the pagination bars are just noise. */
const PAGE_SIZE = 10;

function pageSizeFor(count: number): number | undefined {
  return count > PAGE_SIZE ? PAGE_SIZE : undefined;
}

/** Rows toggle on click; interactive cells swallow it. */
const swallow = (event: MouseEvent): void => event.stopPropagation();

/* ------------------------------------------------------------------------- *
 * Shared props
 * ------------------------------------------------------------------------- */

export interface StatusProps {
  gates: GateMap;
  /** `triage.read` — only then is a link to the triage queue useful. */
  canReadTriage: boolean;
}

/** The triage queue filtered to one instance (its serial number is searchable there). */
function triageLink(row: OverviewRow): string {
  const serial = row.produkt.seriennummer?.trim();
  return serial ? `/triage?q=${encodeURIComponent(serial)}` : '/triage';
}

/* ------------------------------------------------------------------------- *
 * Status — per instance and summarised per group
 * ------------------------------------------------------------------------- */

function InstanceStatus({
  row,
  gate,
  canReadTriage,
}: {
  row: OverviewRow;
  gate: NotifyGate | undefined;
  canReadTriage: boolean;
}): ReactNode {
  const { t } = useLingui();
  if (gate === undefined || gate.kind === 'clean') {
    return (
      <span className="muted" title={t`Ohne KEV-Treffer ist nichts zu melden`}>
        {DASH}
      </span>
    );
  }
  if (gate.kind === 'notified') {
    return (
      <div className="dash-status">
        <Chip tone="good"><Trans>benachrichtigt</Trans></Chip>
        <span className="prod">{fmtDate(gate.versendetAm)}</span>
        {/* The Audit-Log is guarded by triage.read, like the triage links. */}
        {canReadTriage ? (
          <Link to={auditLogLink(row)} onClick={swallow}>
            <Trans>Im Audit-Log ansehen</Trans>
          </Link>
        ) : null}
      </div>
    );
  }
  if (gate.kind === 'ready') {
    if (!gate.kundeResolvable) return <span className="muted"><Trans>kein Kunde zugeordnet</Trans></span>;
    const { openCount } = gate;
    return (
      <div className="dash-status">
        <Chip tone="warn" title={t`Produktbewertung „Betroffen“ bestätigt, Benachrichtigung noch nicht versendet`}>
          <Plural value={openCount} one="# Benachrichtigung offen" other="# Benachrichtigungen offen" />
        </Chip>
        <QueueChips queued={gate.queuedCount} failed={gate.failedCount} />
        {canReadTriage ? <Link to="/benachrichtigungen" onClick={swallow}><Trans>Zur Benachrichtigung</Trans></Link> : null}
      </div>
    );
  }
  if (gate.openEvents > 0) {
    return (
      <div className="dash-status">
        <span className="muted"><Trans>Bewertung ausstehend</Trans></span>
        {canReadTriage ? (
          <Link to={triageLink(row)} onClick={swallow}><Trans>Zur Bewertung</Trans></Link>
        ) : null}
      </div>
    );
  }
  return (
    <span
      className="muted"
      title={
        gate.totalEvents > 0
          ? t`Alle Bewertungen dieser Instanz sind „Nicht betroffen“ oder „Behoben“.`
          : t`Für diese Instanz liegt noch keine Bewertung vor. Der KEV-Scan legt sie an.`
      }
    >
      {gate.totalEvents > 0 ? <Trans>nicht anwendbar</Trans> : <Trans>keine Bewertung</Trans>}
    </span>
  );
}

/** Of the not yet sent notifications: waiting in or failed in the Postausgang (DEV-93). */
function QueueChips({ queued, failed }: Readonly<{ queued: number; failed: number }>): ReactNode {
  const { t } = useLingui();
  return (
    <>
      {queued > 0 ? (
        <Chip tone="info" title={t`Eingereiht, wird aus dem Postausgang versendet`}><Trans>{queued} im Postausgang</Trans></Chip>
      ) : null}
      {failed > 0 ? (
        <Chip tone="alert" title={t`Versand fehlgeschlagen — im Postausgang erneut einreihen oder zurückziehen`}>
          <Trans>{failed} fehlgeschlagen</Trans>
        </Chip>
      ) : null}
    </>
  );
}

/** Share of affected rows: red for affected, green for the rest. */
function AffectedBar({ affected, total }: { affected: number; total: number }): ReactNode {
  if (total <= 0) return null;
  const share = Math.min(100, Math.max(0, (affected / total) * 100));
  return (
    <span className="dash-affected-bar" aria-hidden="true">
      <span style={{ width: `${share}%` }} />
    </span>
  );
}

/** The triage queue searched for the group's customer or product. */
function groupTriageLink(group: OverviewGroup): string {
  return `/triage?q=${encodeURIComponent(group.title)}`;
}

/** Status of a group within its work section. */
function GroupStatus({
  group,
  section,
  canReadTriage,
}: {
  group: OverviewGroup;
  section: WorkState;
  canReadTriage: boolean;
}): ReactNode {
  const { t } = useLingui();
  const count = group.rows.length;
  if (section === 'assess') {
    return (
      <div className="dash-status">
        <Chip tone="warn" title={t`KEV-Treffer ohne bestätigte Produktbewertung`}>
          <Trans>{count} zu bewerten</Trans>
        </Chip>
        {canReadTriage ? <Link to={groupTriageLink(group)} onClick={swallow}><Trans>Zur Bewertung</Trans></Link> : null}
      </div>
    );
  }
  if (section === 'notify') {
    return (
      <div className="dash-status">
        <Chip tone="warn" title={t`Produktbewertung „Betroffen“ bestätigt, Benachrichtigung noch nicht versendet`}>
          <Trans>{count} zu versenden</Trans>
        </Chip>
        {canReadTriage ? <Link to="/benachrichtigungen" onClick={swallow}><Trans>Zur Benachrichtigung</Trans></Link> : null}
      </div>
    );
  }
  if (section === 'notified') {
    const { notified, due } = group;
    return (
      <Chip tone="good" title={t`Bezogen auf alle Instanzen, die eine Benachrichtigung brauchen`}>
        {notified >= due ? <Trans>Alle benachrichtigt</Trans> : <Trans>{notified} von {due} benachrichtigt</Trans>}
      </Chip>
    );
  }
  return group.affected === 0 ? (
    <Chip tone="good"><Trans>keine KEV-Treffer</Trans></Chip>
  ) : (
    <span className="muted" title={t`Alle Bewertungen sind „Nicht betroffen“ oder „Behoben“.`}>
      <Trans>nicht anwendbar</Trans>
    </span>
  );
}

/* ------------------------------------------------------------------------- *
 * Instance table (inside an expanded group)
 * ------------------------------------------------------------------------- */

function instanceLabel(row: OverviewRow): string {
  const version = fmtVersion(row.produktversion.version_nr);
  return [
    row.produkt.seriennummer ? `S/N ${row.produkt.seriennummer}` : null,
    row.produktversion.version_nr ? (version === 'Demo' ? version : `V${version}`) : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

interface InstanceTableProps extends StatusProps {
  group: OverviewGroup;
  store: ExploitabilityStore;
}

function InstanceTable({
  group,
  store,
  gates,
  canReadTriage,
}: InstanceTableProps): ReactNode {
  const { t } = useLingui();
  const columns: DataTableColumn<OverviewRow>[] = [
    {
      key: 'instanz',
      header: t`Produkt`,
      width: '32%',
      sortValue: (row) => row.produkttyp.bezeichnung,
      render: (row) => (
        <>
          <div className="kunde-name">{orFallback(row.produkttyp.bezeichnung, t`Unbekannter Produkttyp`)}</div>
          <div className="prod">{instanceLabel(row) || DASH}</div>
        </>
      ),
    },
    {
      key: 'komponenten',
      header: t`Betroffene Komponenten`,
      width: '30%',
      sortValue: (row) => row.affected_count,
      render: (row) => <ComponentsCell row={row} />,
    },
    {
      key: 'cves',
      header: t`KEV-CVEs`,
      width: '20%',
      sortValue: (row) => row.kev_cves.length,
      render: (row) => <CvesCell cves={row.kev_cves} max={3} />,
    },
    {
      key: 'status',
      header: t`Status`,
      width: '18%',
      render: (row) => (
        <InstanceStatus row={row} gate={gates.get(row.produkt.id)} canReadTriage={canReadTriage} />
      ),
    },
  ];

  return (
    <div className="dash-subwrap">
      <DataTable
        rows={group.rows}
        columns={columns}
        rowKey={(row) => row.produkt.id}
        renderDetail={(row) => <KomponentenDetail row={row} store={store} />}
        mobileBreakpoint={720}
        pageSize={pageSizeFor(group.rows.length)}
        paginationResetKey={group.key}
        resetPageOnRowsChange={false}
        caption={t`Produkte des Kunden`}
      />
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Group table
 * ------------------------------------------------------------------------- */

/** The customer's contact address, or why there is none. */
function GroupContact({ group }: Readonly<{ group: OverviewGroup }>): ReactNode {
  if (group.subtitle !== null) return <><Icon name="mail" size={12} /> {group.subtitle}</>;
  if (group.hasKunde) return <><Icon name="warning" size={12} /> <Trans>keine Kontakt-E-Mail</Trans></>;
  return <Trans>Produktinstanz ohne Kunde</Trans>;
}

export interface GroupTableProps extends StatusProps {
  section: WorkState;
  /** Accessible name of the table, e.g. the section title. */
  caption: string;
  groups: readonly OverviewGroup[];
  store: ExploitabilityStore;
  emptyText: ReactNode;
  paginationResetKey: string;
}

export function GroupTable({
  section,
  caption,
  groups,
  store,
  emptyText,
  paginationResetKey,
  ...status
}: GroupTableProps): ReactNode {
  const { t } = useLingui();
  const columns: DataTableColumn<OverviewGroup>[] = [
    {
      key: 'name',
      header: t`Kunde`,
      width: '34%',
      sortValue: (group) => group.title,
      render: (group) => (
        <>
          <div className="kunde-name">
            {group.title}
            {group.euKunde ? (
              <>
                {' '}
                <Chip tone="eu" title={t`Kunde in der EU: CRA-Meldepflichten gelten`}>
                  EU
                </Chip>
              </>
            ) : null}
          </div>
          <div className="prod">
            <GroupContact group={group} />
          </div>
        </>
      ),
    },
    {
      key: 'betroffen',
      header: t`Betroffen`,
      width: '18%',
      sortValue: (group) => group.affected,
      render: ({ affected, total }) => (
        <>
          <span className="count-pill">{affected}</span>{' '}
          <span className="muted">
            <Plural value={total} one="von # Produkt" other="von # Produkten" />
          </span>
          <AffectedBar affected={affected} total={total} />
        </>
      ),
    },
    {
      key: 'cves',
      header: t`KEV-CVEs`,
      width: '22%',
      sortValue: (group) => group.cves.length,
      render: (group) => <CvesCell cves={group.cves} max={3} />,
    },
    {
      key: 'status',
      header: t`Status`,
      width: '26%',
      headerTitle: t`Benachrichtigungen entstehen mit der bestätigten Produktbewertung „Betroffen“ in der Triage.`,
      sortValue: (group) => (section === 'notified' ? group.notified / Math.max(1, group.due) : group.rows.length),
      render: (group) => <GroupStatus group={group} section={section} canReadTriage={status.canReadTriage} />,
    },
  ];

  return (
    <DataTable
      rows={groups}
      columns={columns}
      rowKey={(group) => group.key}
      renderDetail={(group) => <InstanceTable group={group} store={store} {...status} />}
      emptyText={emptyText}
      mobileBreakpoint={760}
      pageSize={pageSizeFor(groups.length)}
      paginationResetKey={paginationResetKey}
      resetPageOnRowsChange={false}
      caption={caption}
    />
  );
}
